import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAuthContext } from '@auth_module/context/AuthContext';
import { fetchAllFeatureFlags } from '@auth_module/services';
import { useUser } from '@auth_module/hooks/useUser';
import { QUERY_KEYS } from '@constants/queryKeys';

export interface IFeatureFlag {
  id: number;
  feature_name: string;
  flag_active: boolean;
}

export type FeatureFlag =
  | 'uploader'
  | 'spotify-data-studio'
  | 'open_ai_poc'
  | 'megaphone_integration'
  | 'insights-v1.4-segment-builder'
  | 'ihm_starship_integration'
  | 'include_unprocessed'
  | 'megaphone_flightpath_integration'
  | 'megaphone_self_onboarding'
  | 'export_summary_csv'
  | 'megaphone_tracking_pixel'
  | 'chat_ai_v1'
  | 'video_clips_v1'
  | 'theme_toggler'
  | 'hide_episode_details'
  | 'episode_details_promotion_section'
  | 'named_entity_linking'
  | 'insights_overview_page'
  | 'insights_my_library_page'
  | 'edit_user_avatar'
  | 'audio_processing_count'
  | 'filter_episodes_by_show_id'
  | 'aql_language_filter'
  | 'export_show_summary_from_episode_list';

export const FEATURE_FLAG = {
  UPLOADER: 'uploader' as FeatureFlag,
  SPOTIFY_DATA_STUDIO: 'spotify-data-studio' as FeatureFlag,
  OPEN_AI_POC: 'open_ai_poc' as FeatureFlag,
  MEGAPHONE_INTEGRATION: 'megaphone_integration' as FeatureFlag,
  INSIGHTS_V1_4_SEGMENT_BUILDER: 'insights-v1.4-segment-builder' as FeatureFlag,
  IHM_STARSHIP_INTEGRATION: 'ihm_starship_integration' as FeatureFlag,
  INCLUDE_UNPROCESSED: 'include_unprocessed' as FeatureFlag,
  MEGAPHONE_FLIGHTPATH_INTEGRATION: 'megaphone_flightpath_integration' as FeatureFlag,
  MEGAPHONE_SELF_ONBOARDING: 'megaphone_self_onboarding' as FeatureFlag,
  EXPORT_SUMMARY_CSV: 'export_summary_csv' as FeatureFlag,
  MEGAPHONE_TRACKING_PIXEL: 'megaphone_tracking_pixel' as FeatureFlag,
  CHAT_AI_V1: 'chat_ai_v1' as FeatureFlag,
  THEME_TOGGLER: 'theme_toggler' as FeatureFlag,
  HIDE_EPISODE_DETAILS: 'hide_episode_details' as FeatureFlag,
  EPISODE_DETAILS_PROMOTION_SECTION: 'episode_details_promotion_section' as FeatureFlag,
  NAMED_ENTITY_LINKING: 'named_entity_linking' as FeatureFlag,
  INSIGHTS_OVERVIEW_PAGE: 'insights_overview_page' as FeatureFlag,
  INSIGHTS_MY_LIBRARY_PAGE: 'insights_my_library_page' as FeatureFlag,
  EDIT_USER_AVATAR: 'edit_user_avatar' as FeatureFlag,
  AUDIO_PROCESSING_COUNT: 'audio_processing_count' as FeatureFlag,
  FILTER_EPISODES_BY_SHOW_ID: 'filter_episodes_by_show_id' as FeatureFlag,
  AQL_LANGUAGE_FILTER: 'aql_language_filter' as FeatureFlag,
  EXPORT_SHOW_SUMMARY_FROM_EPISODE_LIST: 'export_show_summary_from_episode_list' as FeatureFlag,
};

export const useFeatureFlag = (feature: FeatureFlag) => {
  const { userFeatures, isLoadingUserFeatures } = useUser();

  const { auth } = useAuthContext();

  const { data, isLoading: isLoadingAllFeatures } = useQuery({
    queryKey: [QUERY_KEYS.ALL_FEATURE_FLAGS],
    queryFn: fetchAllFeatureFlags,
    enabled: !!auth.token,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const allFeatures = (data?.data as IFeatureFlag[]) || [];

  const isEnabled = useMemo(() => {
    // if feature flag is not active, return true (feature flag is disabled and that means feature is available)
    if (allFeatures.find((f) => f.feature_name === feature && !f.flag_active)) return true;

    // if feature flag is active check if user has access to it
    return !!userFeatures?.find((f) => f.feature_name === feature);
  }, [userFeatures, feature, allFeatures]);

  // if we are NOT in production, all features are enabled
  // except for the hide_episode_details

  if (
    !(import.meta.env.MODE === 'production' || import.meta.env.MODE === 'production-ihm') &&
    feature !== FEATURE_FLAG.HIDE_EPISODE_DETAILS
  )
    return { isEnabled: true, isLoading: false };

  return { isEnabled, isLoading: isLoadingUserFeatures || isLoadingAllFeatures };
};
